import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { server } from '../../server'; 
import { getStoredAuthToken } from '../../utils/authSession';
import { getStoredCurrentProfile } from '../../utils/shopProfile';
import '../../stylesheets/SocialComposerOverrides.css';

const FALLBACK_AVATAR = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 rx=%2240%22 fill=%22%2323292f%22/%3E%3Ccircle cx=%2240%22 cy=%2231%22 r=%2214%22 fill=%22%236ee000%22/%3E%3Cpath d=%22M17 70c4-16 16-25 23-25s19 9 23 25%22 fill=%22%236ee000%22 opacity=%22.75%22/%3E%3C/svg%3E';

const Rooms = ({
  profileType,
  profileId,
  emptyMessage = 'No Communities Created Yet.',
  showRailControls = false,
  hideTitle = false,
}) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joiningRoomId, setJoiningRoomId] = useState('');
  const [joinError, setJoinError] = useState('');
  const roomsListRef = useRef(null);
  const currentProfile = useSelector((state) => state.user.currentProfile) || getStoredCurrentProfile() || 'User';

  useEffect(() => {
    const fetchRooms = async () => {
      if (!profileType || !profileId) {
        setError('Profile type and ID are required to fetch rooms.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${server}/room/profile-rooms`, {
          params: {
            profileType,
            profileId,
          },
          withCredentials: true,
        });

        if (response.data.success) {
          setRooms(Array.isArray(response.data.rooms) ? response.data.rooms : []);
        } else {
          setError(response.data.message || 'Failed to fetch rooms.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching rooms:", err);
        setError('Failed to fetch rooms.');
        setLoading(false);
      }
    };

    fetchRooms();
  }, [profileType, profileId]);

  const handleJoinRoom = async (event, room) => {
    event.preventDefault();
    event.stopPropagation();

    if (!room?._id || joiningRoomId) {
      return;
    }

    setJoiningRoomId(room._id);
    setJoinError('');

    const token = getStoredAuthToken();
    const headers = { 'current-profile': currentProfile || 'User' };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await axios.post(`${server}/room/rooms/${room._id}/join`, {}, {
        withCredentials: true,
        headers,
      });
      const updatedRoom = response.data.room || {};

      setRooms((currentRooms) => currentRooms.map((currentRoom) => (
        currentRoom._id === room._id
          ? {
              ...currentRoom,
              ...updatedRoom,
              isViewerMember: response.data.status === 'joined' || updatedRoom.isViewerMember,
              hasViewerJoinRequest: response.data.status === 'requested' || updatedRoom.hasViewerJoinRequest,
            }
          : currentRoom
      )));
    } catch (err) {
      setJoinError(err.response?.data?.message || 'Could not join this community.');
    } finally {
      setJoiningRoomId('');
    }
  };

  const scrollRoomsRail = (direction) => {
    const rail = roomsListRef.current;
    if (!rail) {
      return;
    }

    const scrollDistance = Math.max(rail.clientWidth * 0.82, 180);
    rail.scrollBy({ left: direction * scrollDistance, behavior: 'smooth' });
  };

  if (loading) return <p>Loading rooms...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="rooms-container">
      {!hideTitle && <h2>Communities</h2>}
      {rooms.length > 0 ? (
        <div className={`rooms-list-rail-shell${showRailControls && rooms.length > 1 ? '' : ' rooms-list-rail-shell--single'}`}>
          {showRailControls && rooms.length > 1 && (
            <button
              type="button"
              className="rooms-list-rail-arrow rooms-list-rail-arrow--prev"
              aria-label="Scroll communities left"
              onClick={() => scrollRoomsRail(-1)}
            >
              <ChevronLeftIcon aria-hidden="true" />
            </button>
          )}
          <ul className="rooms-list" ref={roomsListRef}>
            {rooms.map(room => {
              const canJoinOrRequestRoom = Boolean(room.canJoin || room.canRequestJoin || room.hasViewerJoinRequest);
              const isJoining = joiningRoomId === room._id;
              const joinLabel = isJoining
                ? 'Joining...'
                : room.hasViewerJoinRequest
                  ? 'Requested'
                  : room.canRequestJoin
                    ? 'Request to Join'
                    : 'Join';

              return (
                <li
                  key={room._id}
                  className="rooms-list-item"
                  style={room.banner?.url ? {
                    backgroundImage: `url(${room.banner.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  } : undefined}
                >
                  <div className={`rooms-list-privacy-badge ${room.privacy === 'private' ? 'private' : 'public'}`}>
                    {room.privacy === 'private' ? 'Private' : 'Public'}
                  </div>
                  <Link to={`/rooms/${room.handle}`} className="rooms-list-card-link">
                    <div className="rooms-list-card-content">
                      <img
                        className="rooms-list-avatar"
                        src={room.avatar?.url || FALLBACK_AVATAR}
                        alt={`${room.name} community logo`}
                      />
                      <div className="room-name">{room.name}</div>
                      {room.description && <div className="room-description">{room.description}</div>}
                    </div>
                  </Link>
                  {canJoinOrRequestRoom && (
                    <button
                      type="button"
                      className="rooms-list-join-button"
                      onClick={(event) => handleJoinRoom(event, room)}
                      disabled={isJoining || Boolean(room.hasViewerJoinRequest)}
                    >
                      {joinLabel}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {showRailControls && rooms.length > 1 && (
            <button
              type="button"
              className="rooms-list-rail-arrow rooms-list-rail-arrow--next"
              aria-label="Scroll communities right"
              onClick={() => scrollRoomsRail(1)}
            >
              <ChevronRightIcon aria-hidden="true" />
            </button>
          )}
        </div>
      ) : (
        <div className="profile-widget-empty-state">
          <p className="profile-widget-empty-message">{emptyMessage}</p>
        </div>
      )}
      {joinError && <p className="rooms-list-join-error">{joinError}</p>}
    </div>
  );
};

export default Rooms;




